import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEmptyBrand } from '../../interfaces/Brand/Brand';
import {
  createBrand,
  deleteBrand,
  getBrand,
  updateBrand,
  updateBrandMedia,
} from '../../api/Brand';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { ImageInput } from '../../components/ImageInput/ImageInput';
import { BrandMediaOptions } from '../../interfaces/Brand/BrandMediaOptions';
import NumberInput from '../../components/NumberInput/NumberInput';
import { MetaCard } from '../../components/Meta/MetaCard';
import { MetaTable } from '../../interfaces/Meta/MetaTable';
import { OpenInFrontend } from '../../components/OpenInFrontend/OpenInFrontend';
import { TranslationAdornment } from '../../components/Translation/TranslationAdornment';

export const ModifyBrandPage = () => {
  const navigate = useNavigate();

  const { brandID: brandIDAsStr } = useParams<string>();

  const brandID = brandIDAsStr ? Number(brandIDAsStr) : undefined;
  const isEditing = Number.isFinite(brandID);

  const [brand, setBrand] = useState(getEmptyBrand());
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshBrand = () => {
    setLoading(true);
    getBrand(brandID!)
      .then((b) => {
        setBrand(b);
        setIsActive(b.isActive);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshBrand();
    }
  }, [brandIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updateBrand(brand)
        .then((updatedBrand) => {
          toast.success('Marka başarıyla güncellendi');

          setBrand(updatedBrand);
          setIsActive(updatedBrand.isActive);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createBrand(brand)
        .then((createdBrand) => {
          toast.success('Marka başarıyla oluşturuldu');
          navigate(`/products/brands/${createdBrand.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleUpdateMedia = (options: BrandMediaOptions) => {
    setLoading(true);
    updateBrandMedia(brand.id, options)
      .then((updatedBrand) => {
        toast.success('Medya başarıyla güncellendi');
        setBrand(updatedBrand);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteBrand(brand.id)
      .then(() => {
        toast.success('Marka başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/products/brands');

  return (
    <Box sx={{ ...sharedStyles.pageBody, gap: 1.5 }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili markayı silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Markayı Düzenle' : 'Yeni Marka'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <>
            <OpenInFrontend
              path='marka'
              slug={brand.slug}
              isActive={isActive}
            />

            <Button
              variant='contained'
              color='error'
              size='small'
              startIcon={<DeleteIcon />}
              onClick={() => setClickedDelete(true)}
              sx={{ ...sharedStyles.buttonText }}
            >
              Sil
            </Button>
          </>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={brand.name}
          onChange={(e) => setBrand({ ...brand, name: e.target.value })}
          fullWidth
          InputProps={{
            endAdornment: (
              <TranslationAdornment
                defaultValue={brand.name}
                value={brand.nameTranslation}
                onChange={(x) => setBrand({ ...brand, nameTranslation: x })}
              />
            ),
          }}
        />
        <NumberInput
          label='Sıra'
          value={brand.sortOrder}
          onChange={(newOrder) => setBrand({ ...brand, sortOrder: newOrder })}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={brand.isActive}
                onChange={() =>
                  setBrand({ ...brand, isActive: !brand.isActive })
                }
              />
            }
            label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={brand.showLabel}
                onChange={() =>
                  setBrand({ ...brand, showLabel: !brand.showLabel })
                }
              />
            }
            label={
              <Typography sx={sharedStyles.subtitle2}>İsmi göster</Typography>
            }
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>
      {isEditing && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <ImageInput
              title='Logo'
              existingImage={brand.logo}
              onUpload={(file) => handleUpdateMedia({ logo: file })}
              onDelete={() => handleUpdateMedia({ eraseLogo: true })}
            />
            <ImageInput
              title='Afiş'
              existingImage={brand.banner}
              onUpload={(file) => handleUpdateMedia({ banner: file })}
              onDelete={() => handleUpdateMedia({ eraseBanner: true })}
            />
          </Box>

          <MetaCard table={MetaTable.BRAND} id={brandID!} />
        </>
      )}
    </Box>
  );
};
